import { useRef, useEffect, useState, useCallback, lazy, Suspense } from 'react';
import type * as monaco from 'monaco-editor';
import { useSettings } from '@/contexts/SettingsContext';
import { useTablesQuery, useFunctionsQuery, useDatabasesQuery, useSchemasQuery } from '@/hooks/useDatabase';
import { AgentProxyService } from '@/services/agentProxyService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SqlCompletionProvider } from './SqlCompletionProvider';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { createSqlFoldingRanges } from '@/utils/sqlParser';
import '@/utils/monacoTestUtils'; // Initialize test utilities
import '@/utils/autocompleteTestUtils'; // Initialize autocomplete test utilities
import '@/utils/sqlAutocompleteTests'; // Initialize comprehensive test suite

// Lazy load the Monaco Editor component
const Editor = lazy(async () => import('@monaco-editor/react'))

interface MonacoSqlEditorProps {
  value: string;
  onChange: (value: string) => void;
  onSelectionChange?: (selection: { startLine: number; startColumn: number; endLine: number; endColumn: number }) => void;
  onEditorMount?: () => void;
  language?: 'mysql' | 'pgsql';
  readOnly?: boolean;
  datasourceId?: string;
  databaseName?: string;
  schemaName?: string;
  databaseType?: string;
}

export function MonacoSqlEditor({
  value,
  onChange,
  onSelectionChange,
  onEditorMount,
  readOnly = false,
  datasourceId = '',
  databaseName = '',
  schemaName,
  databaseType
}: MonacoSqlEditorProps) {
  const { settings } = useSettings();
  const queryClient = useQueryClient();
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const completionProviderRef = useRef<SqlCompletionProvider | null>(null);
  const completionProviderDisposableRef = useRef<monaco.IDisposable | null>(null);
  
  // Track previous datasourceId, databaseName, and schemaName to detect changes
  const prevDatasourceIdRef = useRef<string>(datasourceId);
  const prevDatabaseNameRef = useRef<string>(databaseName);
  const prevSchemaNameRef = useRef<string | undefined>(schemaName);

  // Fetch databases for autocomplete (for suggestions), without per-database details enrichment
  const databasesQuery = useDatabasesQuery(datasourceId, { enrichDetails: false });

  // Fetch schemas for autocomplete (for PostgreSQL)
  const schemasQuery = useSchemasQuery(datasourceId, databaseName, {
    enabled: !!datasourceId && !!databaseName && databaseType === 'PostgreSQL' && isEditorReady
  });

  // Fetch tables for autocomplete with a delay to prioritize editor loading
  // For MySQL, don't pass schemaName as MySQL doesn't use schemas like PostgreSQL
  const effectiveSchemaName = databaseType === 'mysql' ? undefined : schemaName;
  const tablesQuery = useTablesQuery(datasourceId, databaseName, effectiveSchemaName, {
    enabled: !!datasourceId && !!databaseName && isEditorReady,
    databaseType
  });

  // Fetch functions for autocomplete
  // For MySQL, don't pass schemaName as MySQL doesn't use schemas like PostgreSQL
  const functionsQuery = useFunctionsQuery(datasourceId, databaseName, effectiveSchemaName, {
    enabled: !!datasourceId && !!databaseName && isEditorReady,
    databaseType
  });

  // Fetch columns for all tables - deferred until tables are loaded
  const { data: allColumns, error: columnsError, isLoading: columnsLoading } = useQuery({
    queryKey: ['all-columns', datasourceId, databaseName, effectiveSchemaName],
    queryFn: async () => {

      if (!tablesQuery.data?.data || !Array.isArray(tablesQuery.data.data)) {
        return [];
      }

      const tables = tablesQuery.data.data;
      const columnPromises = tables.map(async (table: unknown) => {
        const tableName = typeof table === 'string' ? table : 
          (typeof table === 'object' && table !== null && 
           ('name' in table || 'table_name' in table) ? 
           (table as { name?: string; table_name?: string }).name || 
           (table as { name?: string; table_name?: string }).table_name : '') || '';
        if (!tableName) return { tableName, columns: [] };

        try {
          // Determine schema parameter for PostgreSQL (MySQL doesn't use schemas)
          const tableSchemaName = databaseType === 'PostgreSQL' && schemaName ? schemaName : undefined;

          const response = await AgentProxyService.getColumnInfo(
            datasourceId,
            databaseName,
            tableName,
            tableSchemaName
          );
          
          const columns = Array.isArray(response) ? response : 
            ((response as { columns?: unknown[] }).columns || []);
          
          return {
            tableName,
            columns
          };
        } catch (error) {
          console.error(`🔍 [MonacoSqlEditor] Error fetching columns for table ${tableName}:`, {
            error,
            tableName,
            datasourceId,
            databaseName,
            schemaName,
            databaseType
          });
          return { tableName, columns: [] };
        }
      });

      const results = await Promise.all(columnPromises);
      
      return results;
    },
    enabled: !!tablesQuery.data?.data && tablesQuery.data.data.length > 0 && isEditorReady,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Log columns query error status
  if (columnsError) {
    console.error('🔍 [MonacoSqlEditor] Columns query error:', columnsError);
  }

  // Clear completion provider cache when datasource, database, or schema changes
  useEffect(() => {
    const currentDatasourceId = datasourceId;
    const currentDatabaseName = databaseName;
    const currentSchemaName = schemaName;
    const prevDatasourceId = prevDatasourceIdRef.current;
    const prevDatabaseName = prevDatabaseNameRef.current;
    const prevSchemaName = prevSchemaNameRef.current;
    
    const hasContextChanged = (
      currentDatasourceId !== prevDatasourceId ||
      currentDatabaseName !== prevDatabaseName ||
      currentSchemaName !== prevSchemaName
    );
    
    if (hasContextChanged && (prevDatasourceId || prevDatabaseName)) {

      // Clear the completion provider's internal cache if it exists
      if (completionProviderRef.current) {
        completionProviderRef.current.clearColumnCache();
      }
      
      // Invalidate React Query cache for autocomplete data
      // This ensures stale data from previous context doesn't persist
      queryClient.invalidateQueries({
        predicate: (query) => {
          const { queryKey } = query;
          // Invalidate queries that contain the previous datasource, database, or schema
          return Array.isArray(queryKey) && (
            queryKey.some(key => key === prevDatasourceId) ||
            queryKey.some(key => key === prevDatabaseName) ||
            queryKey.some(key => key === prevSchemaName)
          );
        }
      });

      // Force a fresh setup of autocomplete with new context data
      if (isEditorReady) {
        const monacoInstance = (window as unknown as { monaco?: typeof monaco }).monaco;
        if (monacoInstance) {
          // Small delay to ensure React Query cache invalidation takes effect
          setTimeout(() => {
            // The setupAutocomplete function will be called again due to dependency changes
          }, 100);
        }
      }
    }
    
    // Update the refs for next comparison
    prevDatasourceIdRef.current = currentDatasourceId;
    prevDatabaseNameRef.current = currentDatabaseName;
    prevSchemaNameRef.current = currentSchemaName;
  }, [datasourceId, databaseName, schemaName, queryClient, isEditorReady]);

  // Setup Monaco Editor configuration - simplified for faster initial load
  const handleEditorWillMount = (monacoInstance: typeof monaco) => {
    // Only do minimal setup before mount
    // Register SQL language if not already registered
    const languages = monacoInstance.languages.getLanguages();
    const sqlLanguage = languages.find(lang => lang.id === 'sql');
    
    if (!sqlLanguage) {
      monacoInstance.languages.register({ id: 'sql' });
    }
  };

  // Setup autocomplete provider
  const setupAutocomplete = useCallback((monacoInstance: typeof monaco) => {
    if (!isEditorReady) return;
    
    // Check if autoComplete is disabled in settings
    if (!settings.autoComplete) {
      // If there's an existing provider, we need to dispose it
      if (completionProviderRef.current) {
        completionProviderRef.current = null;
      }
      return;
    }


    // Get table names
    const tables = (tablesQuery.data?.data || []).map((table: unknown) => 
      typeof table === 'string' ? table : 
        (typeof table === 'object' && table !== null && 
         ('name' in table || 'table_name' in table) ? 
         (table as { name?: string; table_name?: string }).name || 
         (table as { name?: string; table_name?: string }).table_name : '') || ''
    ).filter(Boolean);

    // Get columns
    const columns = allColumns || [];

    // Get databases for suggestions
    const databases = (databasesQuery.data || []).map(db => 
      typeof db === 'string' ? db : (db as { name?: string }).name || ''
    ).filter(Boolean);

    // Get schemas for suggestions (PostgreSQL only)
    const schemas = databaseType === 'PostgreSQL' && schemasQuery.data ? 
      schemasQuery.data.map(schema => typeof schema === 'string' ? schema : (schema as { name?: string }).name || '') :
      (schemaName ? [schemaName] : []);

    // Get functions - pass full objects to preserve parameter information
    const functions = functionsQuery.data || [];

    // Combine and deduplicate schemas and databases for suggestions
    const allSuggestions = [...new Set([...schemas, ...databases])].filter(Boolean);
    
    // Debug logging
    if (localStorage.getItem('monaco-debug') === 'true') {
      console.log('Monaco Setup Debug:', {
        schemas,
        databases,
        allSuggestions,
        tables: tables.length,
        columns: columns.length,
        datasourceId,
        databaseName,
        schemaName
      });
    }

    // Dispose existing completion provider to prevent duplicates
    if (completionProviderDisposableRef.current) {
      completionProviderDisposableRef.current.dispose();
      completionProviderDisposableRef.current = null;
    }

    // Create new completion provider
    completionProviderRef.current = new SqlCompletionProvider({
      tables,
      columns,
      schemas: allSuggestions, // Deduplicated schemas and databases
      functions: functions as (string | { name?: string; returnType?: string; language?: string; parameters?: { name?: string; type?: string; mode?: "IN" | "OUT" | "INOUT"; }[]; schema?: string })[],
      datasourceId,
      databaseName,
      schemaName: effectiveSchemaName,
      databaseType
    });

    // Register the completion provider and store the disposable
    completionProviderDisposableRef.current = monacoInstance.languages.registerCompletionItemProvider('sql', completionProviderRef.current);
  }, [isEditorReady, tablesQuery.data, allColumns, functionsQuery.data, databasesQuery.data, schemasQuery.data, schemaName, datasourceId, databaseName, databaseType, settings.autoComplete, effectiveSchemaName]);

  // Cleanup completion provider on unmount
  useEffect(() => {
    return () => {
      if (completionProviderDisposableRef.current) {
        completionProviderDisposableRef.current.dispose();
      }
    };
  }, []);

  // Handle editor mount
  const handleEditorDidMount = (editor: monaco.editor.IStandaloneCodeEditor, monacoInstance: typeof monaco) => {
    editorRef.current = editor;
    setIsEditorReady(true);

    // Defer heavy initialization to improve perceived performance
    requestAnimationFrame(() => {
      // Set SQL language configuration
      monacoInstance.languages.setLanguageConfiguration('sql', {
        comments: {
          lineComment: '--',
          blockComment: ['/*', '*/']
        },
        brackets: [
          ['{', '}'],
          ['[', ']'],
          ['(', ')']
        ],
        autoClosingPairs: [
          { open: '{', close: '}' },
          { open: '[', close: ']' },
          { open: '(', close: ')' },
          { open: '"', close: '"' },
          { open: "'", close: "'" }
        ],
        surroundingPairs: [
          { open: '{', close: '}' },
          { open: '[', close: ']' },
          { open: '(', close: ')' },
          { open: '"', close: '"' },
          { open: "'", close: "'" }
        ]
      });

      // Set basic SQL tokenization
      monacoInstance.languages.setMonarchTokensProvider('sql', {
        tokenizer: {
          root: [
            [/\b(?:SELECT|FROM|WHERE|JOIN|INSERT|UPDATE|DELETE|CREATE|DROP|ALTER|TABLE|INDEX|VIEW|DATABASE|SCHEMA)\b/i, 'keyword'],
            [/\b(?:INT|VARCHAR|TEXT|DATE|TIMESTAMP|BOOLEAN|DECIMAL|FLOAT|DOUBLE)\b/i, 'type'],
            [/\b\d+\b/, 'number'],
            [/'[^']*'/, 'string'],
            [/"[^"]*"/, 'string'],
            [/--.*$/, 'comment'],
            [/\/\*[\s\S]*?\*\//, 'comment'],
            [/[a-zA-Z_][a-zA-Z0-9_]*/, 'identifier'],
            [/[{}()[\]]/, 'bracket'],
            [/[<>=!]+/, 'operator']
          ]
        }
      });

      // Register custom folding range provider for SQL queries with PostgreSQL dollar-quote support
      monacoInstance.languages.registerFoldingRangeProvider('sql', {
        provideFoldingRanges: (model) => {
          const text = model.getValue();
          return createSqlFoldingRanges(text, monacoInstance);
        }
      });

      // Setup autocomplete after a short delay
      setTimeout(() => setupAutocomplete(monacoInstance), 100);
    });

    // Configure editor options
    editor.updateOptions({
      fontSize: settings.fontSize,
      fontFamily: settings.fontFamily,
      lineNumbers: settings.showLineNumbers ? 'on' : 'off',
      lineNumbersMinChars: 10,
      glyphMargin: true,
      folding: true,
      foldingHighlight: true,
      foldingStrategy: 'auto',
      showFoldingControls: 'always',
      lineDecorationsWidth: 40, // Increased space for run buttons
      wordWrap: 'off',
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      automaticLayout: true,
      suggestOnTriggerCharacters: true,
      quickSuggestions: {
        other: true,
        comments: false,
        strings: false
      },
      suggestSelection: 'first',
      tabCompletion: 'on',
      acceptSuggestionOnEnter: 'on',
      snippetSuggestions: 'inline',
      parameterHints: { enabled: true },
      hover: { enabled: true },
      // Enhanced autocomplete settings
      suggest: {
        insertMode: 'replace',
        filterGraceful: true,
        showWords: false, // Disable word-based suggestions to prioritize our custom ones
        localityBonus: true,
        shareSuggestSelections: false,
        snippetsPreventQuickSuggestions: false
      },
      // Enable more aggressive triggering
      wordBasedSuggestions: 'off', // Use only our custom provider
      // Add more trigger characters for autocomplete
      autoIndent: 'full',
      formatOnType: true,
      // Ensure empty content is properly supported
      emptySelectionClipboard: false, // Prevent interference with empty content
      selectOnLineNumbers: true,
      // Ensure editor is fully editable for empty content
      domReadOnly: false
    });

      // Set editor value to ensure it matches the prop - always set on mount
      // Ensure empty values are handled properly
    editor.setValue(value || '');
    
    // Handle value changes
    editor.onDidChangeModelContent(() => {
      const newValue = editor.getValue();
      // Always allow empty content - this is crucial for single-tab deletion
      onChange(newValue);
    });

    // Handle selection changes
    if (onSelectionChange) {
      editor.onDidChangeCursorSelection((e) => {
        const {selection} = e;
        onSelectionChange({
          startLine: selection.startLineNumber,
          startColumn: selection.startColumn,
          endLine: selection.endLineNumber,
          endColumn: selection.endColumn
        });
      });
    }

    // Add global reference for testing - clear previous one first
    if ((window as unknown as { monacoEditor?: { dispose(): void } }).monacoEditor && (window as unknown as { monacoEditor?: unknown }).monacoEditor !== editor) {
      try {
        (window as unknown as { monacoEditor?: { dispose(): void } }).monacoEditor?.dispose();
      } catch {
        // Ignore disposal errors
      }
    }
    (window as unknown as { monacoEditor?: unknown }).monacoEditor = editor;
    
    // Call onEditorMount callback if provided - always call to re-initialize
    if (onEditorMount) {
      onEditorMount();
    }
    
    // Add enhanced test functions for debugging autocomplete
    (window as unknown as { testMonacoAutocomplete?: () => void }).testMonacoAutocomplete = () => {
      console.log('Monaco Autocomplete Test:');
      console.log('Available tables:', (tablesQuery.data?.data || []).map((table: unknown) => 
        typeof table === 'string' ? table : 
        (typeof table === 'object' && table !== null && 
         ('name' in table || 'table_name' in table) ? 
         (table as { name?: string; table_name?: string }).name || 
         (table as { name?: string; table_name?: string }).table_name : '') || ''
      ));
      console.log('Available columns:', allColumns?.map(({ tableName, columns }) => ({
        table: tableName,
        columns: columns.map((col: unknown) => typeof col === 'string' ? col : (col as { name?: string; column_name?: string }).name || (col as { name?: string; column_name?: string }).column_name || '')
      })));
      console.log('Current SQL:', editor.getValue());
      console.log('Cursor position:', editor.getPosition());
      console.log('Completion provider context:', completionProviderRef.current?.getContext());
      
      // Enable debug mode temporarily
      localStorage.setItem('monaco-debug', 'true');
      console.log('Debug mode enabled. Type in the editor to see autocomplete debug info.');
      console.log('Available test commands:');
      console.log('- testMonacoSQL() - Test SQL parsing');
      console.log('- testTableExtraction() - Test table name extraction');
      console.log('- triggerAutocomplete() - Manually trigger autocomplete');
      
      setTimeout(() => {
        localStorage.removeItem('monaco-debug');
        console.log('Debug mode disabled.');
      }, 60000); // Auto-disable after 60 seconds
    };

    // Test table name extraction
    (window as unknown as { testTableExtraction?: () => void }).testTableExtraction = () => {
      const sql = editor.getValue();
      console.log('Testing table extraction for SQL:', sql);
      
      if (completionProviderRef.current) {
        const extractedTables = (completionProviderRef.current as unknown as { extractTableNamesFromQuery?: (sql: string) => string[] }).extractTableNamesFromQuery?.(sql);
        console.log('Extracted tables:', extractedTables);
      }
    };

    // Manually trigger autocomplete
    (window as unknown as { triggerAutocomplete?: () => void }).triggerAutocomplete = () => {
      const position = editor.getPosition();
      if (position) {
        editor.trigger('keyboard', 'editor.action.triggerSuggest', {});
        console.log('Autocomplete triggered at position:', position);
      }
    };

    // Test column fetching for a specific table
    (window as unknown as { testColumnFetch?: (tableName: string) => Promise<void> }).testColumnFetch = async (tableName: string) => {
      if (!tableName) {
        console.log('Usage: testColumnFetch("table_name")');
        return;
      }
      
      console.log(`Testing column fetch for table: ${tableName}`);
      if (completionProviderRef.current) {
        try {
          const columns = await (completionProviderRef.current as unknown as { fetchColumnsForTable?: (tableName: string) => Promise<unknown> }).fetchColumnsForTable?.(tableName);
          console.log(`Columns for ${tableName}:`, columns);
        } catch (error) {
          console.error(`Error fetching columns for ${tableName}:`, error);
        }
      }
    };
  };

  // Update autocomplete when data changes
  useEffect(() => {
    if (isEditorReady && editorRef.current) {
      const monacoInstance = (window as unknown as { monaco?: typeof monaco }).monaco;
      if (monacoInstance) {
        setupAutocomplete(monacoInstance);
      }
    }
  }, [setupAutocomplete, tablesQuery.data, allColumns, functionsQuery.data, databasesQuery.data, schemasQuery.data, isEditorReady, settings.autoComplete]);

  // Update editor value when prop changes
  useEffect(() => {
    if (editorRef.current) {
      const currentEditorValue = editorRef.current.getValue();
      // Allow empty values and ensure proper synchronization
      if (value !== currentEditorValue) {
        // Temporarily disable change listener to prevent circular updates
        const currentValue = value || ''; // Ensure empty string instead of undefined
        editorRef.current.setValue(currentValue);
      }
    }
  }, [value]);

  // Update editor options when settings change
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.updateOptions({
        fontSize: settings.fontSize,
        fontFamily: settings.fontFamily,
        lineNumbers: settings.showLineNumbers ? 'on' : 'off',
        lineNumbersMinChars: 3,
        glyphMargin: true,
        folding: true,
        foldingHighlight: true,
        foldingStrategy: 'auto',
        showFoldingControls: 'always',
        lineDecorationsWidth: 40, // Increased space for run buttons
      });
    }
  }, [settings.fontSize, settings.fontFamily, settings.showLineNumbers]);

  return (
    <div className="h-full w-full overflow-hidden">
      <style>
        {`
          /* Position folding controls specifically to the far right side */
          .monaco-editor .margin .margin-view-overlays .cldr {
            position: absolute;
            right: 2px !important;
            left: auto !important;
            width: auto !important;
            text-align: right;
          }
          
          /* Ensure folding decorations are right-aligned within their container */
          .monaco-editor .folding-decoration {
            float: right;
            margin-right: 2px;
            position: absolute;
            right: 0;
          }
          
          /* Adjust the glyph margin to accommodate right-aligned folding */
          .monaco-editor .glyph-margin {
            width: 50px;
          }
          
          /* Position the actual folding widget to the far right */
          .monaco-editor .codicon-chevron-down,
          .monaco-editor .codicon-chevron-right {
            position: absolute;
            right: 2px !important;
            left: auto !important;
          }
          
          /* Specifically target the folding widget container */
          .monaco-editor .margin-view-overlays .current-line-margin {
            position: relative;
          }
          
          /* Override any left positioning for folding elements */
          .monaco-editor .folding {
            right: 2px !important;
            left: auto !important;
            position: absolute;
          }
          
          /* Ensure the folding area doesn't interfere with run buttons */
          .monaco-editor .decorationsOverviewRuler {
            right: 0;
          }
        `}
      </style>
      <Suspense 
        fallback={
          <div className="h-full w-full flex items-center justify-center bg-white dark:bg-gray-900">
            <LoadingSpinner size="lg" text="Loading SQL Editor..." />
          </div>
        }
      >
        <Editor
          height="100%"
          defaultLanguage="sql"
          defaultValue={value || ''}
          theme={settings.theme === 'dark' ? 'vs-dark' : 'vs'}
          options={{
              readOnly,
              fontSize: settings.fontSize,
              fontFamily: settings.fontFamily,
              lineNumbers: settings.showLineNumbers ? 'on' : 'off',
              lineNumbersMinChars: 3,
              glyphMargin: true,
              folding: true,
              foldingHighlight: true,
              foldingStrategy: 'auto',
              showFoldingControls: 'always',
              lineDecorationsWidth: 40, // Increased space for run buttons
              wordWrap: 'off',
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              suggestOnTriggerCharacters: settings.autoComplete,
              quickSuggestions: settings.autoComplete ? {
                other: true,
                comments: false,
                strings: false
              } : false,
              suggestSelection: 'first',
              tabCompletion: settings.autoComplete ? 'on' : 'off',
              acceptSuggestionOnEnter: settings.autoComplete ? 'on' : 'off',
              snippetSuggestions: settings.autoComplete ? 'inline' : 'none',
              parameterHints: { enabled: true },
              hover: { enabled: true },
              contextmenu: true,
              mouseWheelZoom: true,
              selectOnLineNumbers: true,
              roundedSelection: false,
              cursorSmoothCaretAnimation: 'on',
              cursorBlinking: 'blink',
              renderWhitespace: 'boundary',
              renderControlCharacters: false,
              fontLigatures: true,
              smoothScrolling: true,
              padding: { top: 10, bottom: 10 }
            }}
          beforeMount={handleEditorWillMount}
          onMount={handleEditorDidMount}
          loading={
            <div className="h-full w-full flex items-center justify-center bg-white dark:bg-gray-900">
              <LoadingSpinner size="lg" text="Loading SQL Editor..." />
            </div>
          }
        />
      </Suspense>
      
      {/* Loading indicators */}
      {(() => {
        // Check if any queries are actually loading
        const queriesLoading = tablesQuery.isLoading || functionsQuery.isLoading || databasesQuery.isLoading || schemasQuery.isLoading;
        
        // Check if we should show loading for columns
        // Only show columns loading if:
        // 1. There are tables to load columns for
        // 2. The columns query is actually loading (not just disabled)
        const hasTablesForColumns = tablesQuery.data?.data && Array.isArray(tablesQuery.data.data) && tablesQuery.data.data.length > 0;
        const shouldShowColumnsLoading = hasTablesForColumns && columnsLoading;
        
        return queriesLoading || shouldShowColumnsLoading;
      })() && (
        <div className="absolute top-2 right-2 text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-1 rounded shadow-sm">
          Loading suggestions...
        </div>
      )}
      
      {/* Show helpful message when no suggestions are available */}
      {(() => {
        // Only show this message if:
        // 1. No queries are loading
        // 2. Tables query has completed successfully 
        // 3. No tables were found
        const allQueriesCompleted = !tablesQuery.isLoading && !functionsQuery.isLoading && !databasesQuery.isLoading && !schemasQuery.isLoading && !columnsLoading;
        const tablesQueryCompleted = tablesQuery.data !== undefined;
        const noTablesFound = tablesQuery.data?.data && Array.isArray(tablesQuery.data.data) && tablesQuery.data.data.length === 0;
        const shouldShowNoSuggestionsMessage = allQueriesCompleted && tablesQueryCompleted && noTablesFound && datasourceId && databaseName;
        
        return shouldShowNoSuggestionsMessage;
      })() && (
        <div className="absolute top-2 right-2 text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-1 rounded shadow-sm">
          No tables found in database
        </div>
      )}
    </div>
  );
}
